import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { AddNewCostCodeDialog, AddNewTradeDialog } from '@/shared/forms';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { useProjectUsers, useSubmittalMutations, useSubmittalPackages } from '../hooks';
import { useCostCodes, useCreateCostCode, useCreateTrade, useTrades } from '@/features/punch-lists/hooks';
import type { SubmittalItem, SubmittalPriority, SubmittalType } from '../types';
import { SUBMITTAL_PRIORITY_LABELS, SUBMITTAL_TYPE_LABELS } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number | undefined;
  item?: SubmittalItem | null;
}

const TYPES = Object.keys(SUBMITTAL_TYPE_LABELS) as SubmittalType[];
const PRIORITIES = Object.keys(SUBMITTAL_PRIORITY_LABELS) as SubmittalPriority[];

export const SubmittalFormDialog: React.FC<Props> = ({ open, onClose, projectId, item }) => {
  const isEdit = !!item;
  const { data: packages = [] } = useSubmittalPackages(projectId);
  const { data: users = [] } = useProjectUsers(projectId);
  const { data: trades = [] } = useTrades();
  const { data: costCodes = [] } = useCostCodes();
  const createTrade = useCreateTrade();
  const createCostCode = useCreateCostCode();
  const { createItem, updateItem, createPackage } = useSubmittalMutations(projectId);

  const [form, setForm] = useState({
    package: 'unpackaged',
    title: '',
    description: '',
    specification_section: '',
    trade: '',
    cost_code: '',
    discipline: '',
    submittal_type: 'SHOP_DRAWING' as SubmittalType,
    responsible_contractor: '',
    submittal_manager: 'none',
    due_date: '',
    required_response_date: '',
    priority: 'MEDIUM' as SubmittalPriority,
  });
  const [newPackageTitle, setNewPackageTitle] = useState('');

  useEffect(() => {
    if (!item) {
      setForm({
        package: 'unpackaged',
        title: '',
        description: '',
        specification_section: '',
        trade: '',
        cost_code: '',
        discipline: '',
        submittal_type: 'SHOP_DRAWING',
        responsible_contractor: '',
        submittal_manager: 'none',
        due_date: '',
        required_response_date: '',
        priority: 'MEDIUM',
      });
      return;
    }
    setForm({
      package: item.package ? item.package.id : 'unpackaged',
      title: item.title,
      description: item.description ?? '',
      specification_section: item.specification_section ?? '',
      trade: item.trade ? item.trade.id : '',
      cost_code: item.cost_code ? item.cost_code.id : '',
      discipline: item.discipline ?? '',
      submittal_type: item.submittal_type,
      responsible_contractor: item.responsible_contractor ?? '',
      submittal_manager: item.submittal_manager ? String(item.submittal_manager.id) : 'none',
      due_date: item.due_date ?? '',
      required_response_date: item.required_response_date ?? '',
      priority: item.priority,
    });
  }, [item, open]);

  const canSubmit = useMemo(() => !!projectId && !!form.title.trim(), [projectId, form.title]);

  const handleCreatePackage = async () => {
    if (!projectId || !newPackageTitle.trim()) return;
    const created = await createPackage.mutateAsync({
      project: projectId,
      title: newPackageTitle.trim(),
      discipline: form.discipline.trim() || undefined,
    });
    setForm((prev) => ({ ...prev, package: created.id }));
    setNewPackageTitle('');
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    setForm((prev) => ({ ...prev, trade: newTrade.id }));
  };

  const handleAddNewCostCode = async (value: string) => {
    const parsed = JSON.parse(value) as { code: string; name: string };
    const newCostCode = await createCostCode.mutateAsync(parsed);
    setForm((prev) => ({ ...prev, cost_code: newCostCode.id }));
  };

  const handleSubmit = async () => {
    if (!projectId || !canSubmit) return;
    const payload = {
      project: projectId,
      package: form.package === 'unpackaged' ? null : form.package,
      title: form.title.trim(),
      description: form.description.trim(),
      specification_section: form.specification_section.trim(),
      trade: form.trade || null,
      cost_code: form.cost_code || null,
      discipline: form.discipline.trim(),
      submittal_type: form.submittal_type,
      responsible_contractor: form.responsible_contractor.trim(),
      submittal_manager: form.submittal_manager === 'none' ? null : Number(form.submittal_manager),
      due_date: form.due_date || null,
      required_response_date: form.required_response_date || null,
      priority: form.priority,
    };
    if (isEdit && item) {
      await updateItem.mutateAsync({ id: item.id, data: payload });
    } else {
      await createItem.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Submittal' : 'Create Submittal Draft'}</DialogTitle>
          <DialogDescription>
            Capture product approvals, shop drawings, and technical submissions before procurement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Example: HVAC rooftop unit submittal"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Describe scope, references, and expected review context."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Package</Label>
              <Select value={form.package} onValueChange={(value) => setForm((prev) => ({ ...prev, package: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select package" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpackaged">Unpackaged</SelectItem>
                  {packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      {pkg.package_id} - {pkg.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input
                  placeholder="Quick create package"
                  value={newPackageTitle}
                  onChange={(e) => setNewPackageTitle(e.target.value)}
                />
                <Button variant="outline" onClick={handleCreatePackage} disabled={!newPackageTitle.trim()}>
                  Add
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Submittal Type</Label>
              <Select
                value={form.submittal_type}
                onValueChange={(value) => setForm((prev) => ({ ...prev, submittal_type: value as SubmittalType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {SUBMITTAL_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(value) => setForm((prev) => ({ ...prev, priority: value as SubmittalPriority }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {SUBMITTAL_PRIORITY_LABELS[priority]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Spec Section / CSI</Label>
              <Input
                value={form.specification_section}
                onChange={(e) => setForm((prev) => ({ ...prev, specification_section: e.target.value }))}
                placeholder="Example: 23 73 00"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cost Code</Label>
              <CustomCombobox
                options={costCodes.map((c) => ({ value: c.id, label: `${c.code} - ${c.name}` }))}
                value={form.cost_code}
                onChange={(value) => setForm((prev) => ({ ...prev, cost_code: value || '' }))}
                placeholder="Select cost code"
                searchPlaceholder="Search cost codes..."
                addNewLabel="Add New Cost Code"
                emptyMessage="No cost codes found."
                onAddNew={handleAddNewCostCode}
                AddNewDialog={AddNewCostCodeDialog}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Trade</Label>
              <CustomCombobox
                options={trades.map((t) => ({ value: t.id, label: t.name }))}
                value={form.trade}
                onChange={(value) => setForm((prev) => ({ ...prev, trade: value || '' }))}
                placeholder="Select trade"
                searchPlaceholder="Search trades..."
                addNewLabel="Add New Trade"
                emptyMessage="No trades found."
                onAddNew={handleAddNewTrade}
                AddNewDialog={AddNewTradeDialog}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Discipline</Label>
              <Input
                value={form.discipline}
                onChange={(e) => setForm((prev) => ({ ...prev, discipline: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Responsible Contractor</Label>
              <Input
                value={form.responsible_contractor}
                onChange={(e) => setForm((prev) => ({ ...prev, responsible_contractor: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Submittal Manager</Label>
              <Select
                value={form.submittal_manager}
                onValueChange={(value) => setForm((prev) => ({ ...prev, submittal_manager: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm((prev) => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Required Response Date</Label>
            <Input
              type="date"
              value={form.required_response_date}
              onChange={(e) => setForm((prev) => ({ ...prev, required_response_date: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isEdit ? 'Save Changes' : 'Create Draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
